import { FormEvent, useState } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { api, ApiError } from "../lib/api";
import { useAuth } from "../lib/auth-context";

interface LocationState {
  code?: string;
}

export default function EnterBlazerid() {
  const { me, refresh } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const state = (location.state || {}) as LocationState;
  const code = state.code;

  const [blazerid, setBlazerid] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!me) return null; // loading
  if (me.signed_in) return <Navigate to="/practice" replace />;
  // No code carried in router state (direct nav, refresh, or back-button):
  // bounce back to step 1.
  if (!code) return <Navigate to="/access-code" replace />;

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      await api.enter(code!, blazerid.trim());
      await refresh();
      navigate("/practice", { replace: true });
    } catch (err) {
      if (err instanceof ApiError && err.status === 400) {
        const detail = (err.detail as { detail?: string })?.detail;
        setError(detail || "Please check your BlazerID and try again.");
      } else if (err instanceof ApiError && err.status === 403) {
        setError("Access code expired. Please re-enter it.");
        setTimeout(() => navigate("/access-code", { replace: true }), 1200);
      } else {
        setError("Something went wrong. Try again.");
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="page">
      <div className="stack-lg">
        <header>
          <h1 className="h1">Your BlazerID</h1>
          <p className="lede">
            We tag every question you answer so your professor can match the
            pilot data to the class roster after the semester. No password;
            just your BlazerID.
          </p>
        </header>

        <form onSubmit={onSubmit} className="card stack">
          <label htmlFor="blazerid" style={{ fontWeight: 500 }}>
            BlazerID
          </label>
          <input
            id="blazerid"
            className="input"
            type="text"
            autoFocus
            autoComplete="username"
            placeholder="e.g. jblazer"
            value={blazerid}
            onChange={(e) => setBlazerid(e.target.value)}
            style={{ fontFamily: "var(--font-mono)" }}
            disabled={submitting}
            spellCheck={false}
            autoCapitalize="off"
          />
          <p className="muted" style={{ fontSize: 13, margin: 0 }}>
            Letters, digits, and . _ - only. We lowercase it so the same
            student always resolves to the same identifier.
          </p>
          {error && <div className="muted" style={{ color: "var(--negative)" }}>{error}</div>}
          <button className="btn btn--lg" type="submit" disabled={submitting || !blazerid.trim()}>
            {submitting ? "Setting up…" : "Start practicing"}
          </button>
        </form>
      </div>
    </div>
  );
}
